"use client";

import Image from "next/image";
import Link from "@/components/ui/Link";
import { useRouter } from "next/navigation";
import { MessageIcon } from "../ui/message-icon";
import { BOOKMARK_CATEGORIES } from "@/lib/api/public-client";
import { FALLBACK_IMAGE } from '@/lib/constants';
import { getImageUrl, getTimeAgo, getFocusPointStyle, ImageMetadata, BLUR_PLACEHOLDER } from '@/lib/componentutills';

type FieldConfig = {
  hidden?: boolean;
  value?: string;
  customClass?: string;
};

type Props = {
  category?: FieldConfig;
  sectionUrl?: string;
  title?: FieldConfig;
  excerpt?: FieldConfig;
  timeAgo?: FieldConfig;
  imageSrc?: FieldConfig;
  slug?: string;
  imageMetadata?: ImageMetadata;
};

export default function HorizontalBigVideoCategoryNewsBlack({
  category = { hidden: false, value: "BUSINESS", customClass: "" },
  sectionUrl,
  title = { hidden: false, value: "பெருநகரில் மெட்ரோ சர்வதேச நேரத்தில் மணி: ஆகஸ்டில் தொடக்கம்", customClass: "" },
  excerpt = { hidden: false, value: "சென்னையில் பெருநகரில் மெட்ரோ சர்வதேச நேரத்தில் மணி ஆகஸ்ட் மாதம் முதல் அறிமுகம் செய்யப்பட உள்ளது.", customClass: "" },
  timeAgo = { hidden: false, value: "Wed, 17 May 2023", customClass: "" },
  imageSrc = { hidden: false, value: FALLBACK_IMAGE, customClass: "" },
  slug,
  imageMetadata,
}: Props) {
  const router = useRouter();

  const titleValue = title?.value || "";
  const excerptValue = excerpt?.value || "";
  const imageSrcValue = getImageUrl(imageSrc?.value) || FALLBACK_IMAGE;
  const categoryValue = category?.value || "";
  const timeAgoValue = getTimeAgo(timeAgo?.value);

  const handleCategoryClick = (e: React.MouseEvent) => {
    if (sectionUrl) {
      e.preventDefault();
      e.stopPropagation();
      router.push(sectionUrl);
    }
  };

  const content = (
    <article className="bg-transparent p-4 mb-0 align-center">
      <div className="flex gap-4">
        {/* LEFT: VIDEO THUMBNAIL */}
        {!imageSrc?.hidden && (
          <div className={`flex-shrink-0 ${imageSrc?.customClass || ""}`}>
            <div className="relative w-[106px] h-[76px] md:w-[150px] md:h-[110px] lg:w-[180px] lg:h-[130px] overflow-hidden">
              <Image
                src={imageSrcValue}
                alt={titleValue}
                fill
                sizes="(max-width: 768px) 100px, 300px"
                className="object-cover"
                style={getFocusPointStyle(imageMetadata)}
                loading="lazy"
                placeholder="blur"
                blurDataURL={BLUR_PLACEHOLDER}
              />
            </div>
          </div>
        )}

        {/* RIGHT: CONTENT */}
        <div className="flex flex-col justify-between gap-2 w-full">
          <div className="flex flex-col gap-1 md:gap-2">
            {/* Category */}
            {!category?.hidden && (
              <>
                {sectionUrl ? (
                  <span
                    className={`text-11-tamil-600 text-012061 tracking-wide uppercase hover:underline cursor-pointer ${category?.customClass || ""}`}
                    onClick={handleCategoryClick}
                  >
                    {categoryValue}
                  </span>
                ) : (
                  <div className={`text-11-tamil-600 text-012061 tracking-wide uppercase ${category?.customClass || ""}`}>
                    {categoryValue}
                  </div>
                )}
              </>
            )}

            {/* Title */}
            {!title?.hidden && (
 <h3 className={`text-14-tamil-700 text-1B1B1B leading-[1.4] ${title?.customClass || ""}`}>
                {titleValue}
              </h3>
            )}

            {/* Excerpt */}
            {!excerpt?.hidden && (
              <p className={`text-12-tamil-400 text-4D4D4D leading-[1.3] line-clamp-2 opacity-70 ${excerpt?.customClass || ""}`}>
                {excerptValue}
              </p>
            )}
          </div>

          {/* Date */}
          <div className="flex items-center justify-between">
            {!timeAgo?.hidden && (
              <span className={`text-12-tamil-500 text-1B1B1B ${timeAgo?.customClass || ""}`}>{timeAgoValue}</span>
            )}
            <MessageIcon
              id="small-image-news-vert-message-icon"
              variant="message-icon-4"
              slug={slug}
              category={BOOKMARK_CATEGORIES.VIDEOS}
              title={titleValue}
              thumbnailUrl={imageSrcValue}
              description={excerptValue}
              articleUrl={slug ? `/${slug}` : undefined}
            />
          </div>
        </div>
      </div>
    </article>
  );

  if (slug) {
    return (
      <Link href={`/${slug}`} className="block  transition-opacity cursor-pointer">
        {content}
      </Link>
    );
  }

  return content;
}
